import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetAreaRecordGeoItemsDocument,
  UpdateRecordGeoItemDocument,
  type UpdateRecordGeoItemMutation,
  type UpdateRecordGeoItemMutationVariables,
} from "~/features/recordGeoItems/gqls";
import type { RecordGeoItemEvidence, RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseRecordGeoItemEditProps {
  recordId: string;
  geoItemId: string;
  /** false leaves the record_geo_item properties (heading, period, memo) out of both mutations. */
  withDescription: boolean;
  onEdited: () => void;
}

type EvidenceWithReference = RecordGeoItemEvidence & { reference: { id: string; title: string } };

/**
 * Update an existing geo-item area. Issues the paired `cnutUpdateGeoItem`
 * (h3Indexes/geometry) + `cnutUpdateRecordGeoItem` (record metadata) mutation.
 */
export function useRecordGeoItemEdit({ recordId, geoItemId, withDescription, onEdited }: UseRecordGeoItemEditProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [updateRecordGeoItem] = useMutation<UpdateRecordGeoItemMutation, UpdateRecordGeoItemMutationVariables>(
    UpdateRecordGeoItemDocument,
  );

  const submit = useCallback(
    async (formData: RecordGeoItemFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      const i18nLabel = { ja: formData.jaLabel ?? undefined, en: formData.enLabel ?? undefined };
      try {
        await updateRecordGeoItem({
          // Refresh the record's area list so edited label/date show without a manual reload (V1 parity).
          refetchQueries: [{ query: GetAreaRecordGeoItemsDocument, variables: { tenantId, recordId } }],
          awaitRefetchQueries: true,
          variables: {
            input: {
              tenantId,
              geoItemId,
              entityOrRelationId: recordId,
              ...(withDescription && {
                startDate: formData.date,
                endDate: formData.endDate,
                i18nLabel,
                metadata: { memo: formData.memo ?? undefined },
              }),
              evidences: formData.evidences
                .filter((e): e is EvidenceWithReference => Boolean(e.reference?.id))
                .map((e) => ({ id: e.id, referenceId: e.reference.id, citation: e.citation })),
            },
            geoItemInput: {
              tenantId,
              id: geoItemId,
              entityOrRelationId: recordId,
              h3Indexes: formData.h3Indexes,
              geometry: formData.geometry,
              ...(withDescription && {
                recordI18nLabel: i18nLabel,
                recordStartDate: formData.date,
                recordEndDate: formData.endDate,
              }),
            },
          },
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "geoItem" }));
        onEdited();
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [updateRecordGeoItem, geoItemId, recordId, withDescription, onEdited, t, tenantId],
  );

  return { submit, processing };
}
